import React, { useState, useMemo } from "react";
import { Menu, ChevronDown, Settings, LogOut, Building2, X } from "lucide-react";
import { useAgency } from "@/contexts/AgencyContext";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Sheet, SheetContent, SheetHeader, SheetTrigger, SheetClose } from "../../sheet";
import { LeftNavigationData } from "@/data/LeftNavigation";
import { usePermissions } from "@/hooks/usePermissions";
import { Link, useNavigate, useLocation } from "react-router-dom";
import clsx from "clsx";
import { Popover, PopoverContent, PopoverTrigger } from "../../popover";
import { APP_VERSION } from "@/config/version";
import { ThemeToggle } from "../ThemeToggle";
import { NotificationsHub } from "@/components/notifications/NotificationsHub";
import { getAgencyLogoUrl } from "@/utils/agencyLogo";
import { useTranslation } from "react-i18next";

const generateUserAvatar = (name: string): string => {
  const initials = name
    .split(" ")
    .map((word) => word.charAt(0))
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const backgroundColor = "#94a3b8";
  const textColor = "#ffffff";

  return `data:image/svg+xml,${encodeURIComponent(`
    <svg width="40" height="40" xmlns="http://www.w3.org/2000/svg">
      <circle cx="20" cy="20" r="20" fill="${backgroundColor}"/>
      <text x="20" y="26" text-anchor="middle" fill="${textColor}" font-family="Arial" font-size="14" font-weight="bold">
        ${initials}
      </text>
    </svg>
  `)}`;
};

const LogoutButton: React.FC<{
  onLogout: () => Promise<void>;
  className?: string;
}> = ({ onLogout, className }) => {
  const { t } = useTranslation("common");
  return (
    <button onClick={onLogout} className={className}>
      <LogOut size={16} />
      <p className="text-[14px] font-medium">Sair</p>
    </button>
  );
};

const Navibar: React.FC = () => {
  const { t } = useTranslation("common");
  const location = useLocation();
  const navigate = useNavigate();
  const { agency } = useAgency();
  const { user, logout } = useAuth();
  const { isDark } = useTheme();
  const { hasPermission } = usePermissions();
  const [openUserMenu, setOpenUserMenu] = useState(false);

  const handleLogout = async () => {
    await logout();
    setOpenUserMenu(false);
  };

  const handleManageAgency = () => {
    navigate("/agency");
    setOpenUserMenu(false);
  };

  const handleEditProfile = () => {
    navigate("/settings?tab=perfil");
    setOpenUserMenu(false);
  };

  const filteredMainNav = useMemo(() => {
    return LeftNavigationData.filter((item) => {
      if (!item.permission) return true;
      return hasPermission(item.permission);
    });
  }, [hasPermission]);

  const TRANSPARENT_PIXEL =
    "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=";
  const logoSrc = getAgencyLogoUrl(agency, isDark, TRANSPARENT_PIXEL);

  return (
    <div className="w-full min-h-[72px] md:px-6 px-4 border-b border-border flex justify-between items-center bg-card">
      <div className="flex items-center gap-4 min-w-0">
        <img
          src={logoSrc}
          className="max-w-[120px] max-h-[36px] object-contain shrink-0"
          alt={agency?.company_name || "Logo"}
          onError={(e) => {
            e.currentTarget.src = TRANSPARENT_PIXEL;
          }}
        />
      </div>

      <div className="flex items-center gap-3 sm:gap-4">
        <ThemeToggle className="hidden sm:flex" />
        <NotificationsHub />
        <Popover open={openUserMenu} onOpenChange={setOpenUserMenu}>
          <PopoverTrigger asChild>
            <button className="hidden sm:flex items-center gap-3 hover:opacity-80 transition-opacity cursor-pointer">
              <div className="w-[40px] h-[40px] rounded-full overflow-hidden shrink-0">
                <img
                  src={user?.name ? generateUserAvatar(user.name) : "/user.svg"}
                  className="w-full h-full object-cover"
                  alt={user?.name || "User"}
                />
              </div>
              <div className="flex flex-col items-start">
                <p className="text-sm font-semibold text-foreground leading-tight">
                  {user?.name}
                </p>
                <p className="text-xs text-muted-foreground leading-tight">
                  {user?.email}
                </p>
              </div>
              <ChevronDown size={16} className="text-muted-foreground" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="!bg-card p-2 w-[220px] flex flex-col gap-1 border-border border mr-4 shadow-md">
            {user?.type === "agency_user" && (
              <button
                onClick={handleManageAgency}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-[12px] hover:bg-hover transition-colors text-foreground"
              >
                <Building2 size={16} />
                <span className="text-sm">{t("shared.manageAgency")}</span>
              </button>
            )}
            <button
              onClick={handleEditProfile}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-[12px] hover:bg-hover transition-colors text-foreground"
            >
              <Settings size={16} />
              <span className="text-sm">Editar Perfil</span>
            </button>
            <div className="h-px bg-border my-1" />
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-[12px] hover:bg-hover transition-colors text-red-500"
            >
              <LogOut size={16} />
              <span className="text-sm">Sair</span>
            </button>
          </PopoverContent>
        </Popover>

        <div className="sm:hidden">
          <Sheet>
            <SheetTrigger>
              <Menu size={24} className="text-foreground" />
            </SheetTrigger>
            <SheetContent className="bg-card">
              <SheetHeader className="w-full justify-center items-center flex flex-col">
                <SheetClose className="w-full flex justify-end">
                  <X size={28} className="text-foreground" />
                </SheetClose>
                <div className="flex flex-col items-center gap-2 mt-4">
                  <div className="w-[100px] h-[100px] rounded-full overflow-hidden">
                    <img
                      src={user?.name ? generateUserAvatar(user.name) : "/user.svg"}
                      className="w-full h-full object-cover"
                      alt={user?.name || "User"}
                    />
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-medium text-foreground">
                      {user?.name}
                    </p>
                    <p className="text-sm text-muted-foreground">{user?.email}</p>
                  </div>
                  <ThemeToggle className="mt-3" />
                </div>
              </SheetHeader>
              <div className="w-full flex gap-3 flex-col flex-1 overflow-y-auto">
                {filteredMainNav.map((item, index) => {
                  const isActive = item.path === "/"
                    ? location.pathname === "/"
                    : location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
                  return (
                    <div key={index} className="relative flex flex-col px-5 w-full">
                      <SheetClose asChild>
                          <Link
                            to={item.path}
                            className={clsx(
                              "flex w-full gap-3 items-center px-3 min-h-[48px] rounded-[12px] transition-colors duration-200",
                              isActive
                                ? "bg-[var(--sidebar-active-bg)] text-[var(--sidebar-active-fg)]"
                                : "hover:bg-hover text-muted-foreground hover:text-foreground"
                            )}
                          >
                            <div className="flex-shrink-0">{item.icon}</div>
                            <p className="text-[14px] font-medium">{item.title}</p>
                          </Link>
                      </SheetClose>
                      {!!item.children?.length && isActive && (
                        <div className="ml-6 mt-1 flex flex-col border-l border-border pl-2">
                          {item.children.map((child) => (
                            <SheetClose asChild key={child.path}>
                              <Link
                                to={child.path}
                                className={clsx(
                                  "rounded-lg px-3 py-2 text-xs",
                                  location.pathname === child.path
                                    ? "text-foreground bg-hover"
                                    : "text-muted-foreground hover:text-foreground"
                                )}
                              >
                                {child.title}
                              </Link>
                            </SheetClose>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
                <div className="mt-4 px-5">
                  <SheetClose asChild>
                    <LogoutButton
                      onLogout={logout}
                      className="flex w-full gap-3 items-center px-3 min-h-[48px] rounded-[12px] transition-colors hover:bg-hover text-muted-foreground hover:text-foreground"
                    />
                  </SheetClose>
                </div>
                <div className="mt-auto pt-4 px-5 text-center text-muted-foreground text-xs">
                  v{APP_VERSION}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </div>
  );
};

export default Navibar;
